/**
 * ============================================================================
 * RequalificationDashboard — Phase 6e frontend (Equipment & Validation).
 * Owner: Claude 3 (Frontend & UX).
 * Route: /equipment/requalification-schedule
 * Purpose: three-column Kanban view of requalifications. PENDING tasks
 *          surface a "Start" CTA; IN_PROGRESS tasks surface a "Complete"
 *          CTA that asks for a signed REQUAL qualification document id;
 *          COMPLETED tasks are read-only.
 *
 * UX RULES:
 *   - Cards sort by scheduled_for_utc ascending within each column so the
 *     most-urgent work is at the top.
 * ============================================================================ */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardBody, PageHero } from '@edms/design-system';
import wstyles from '../WebhookAdmin/WebhookAdmin.module.css';
import styles from './RequalificationDashboard.module.css';
import {
  useRequalificationScheduleQuery,
  type RequalScheduleRow,
} from './useRequalificationScheduleQuery';
import { RequalKanbanColumn } from './RequalKanbanColumn';
import { StartRequalificationDialog } from './StartRequalificationDialog';
import { CompleteRequalificationDialog } from './CompleteRequalificationDialog';
import type { RequalificationState } from '../EquipmentDetail/useEquipmentDetailQuery';

type DialogState =
  | { kind: 'closed' }
  | { kind: 'start'; row: RequalScheduleRow }
  | { kind: 'complete'; row: RequalScheduleRow };

const STATES: RequalificationState[] = ['PENDING', 'IN_PROGRESS', 'COMPLETED'];

const STATE_DEFAULT_TITLE: Record<RequalificationState, string> = {
  PENDING: 'Pending',
  IN_PROGRESS: 'In progress',
  COMPLETED: 'Completed',
};

export function RequalificationDashboard() {
  const { t } = useTranslation();
  const scheduleQ = useRequalificationScheduleQuery();
  const [dialog, setDialog] = useState<DialogState>({ kind: 'closed' });

  const items = scheduleQ.data?.items ?? [];
  const counts = scheduleQ.data?.counts ?? {
    PENDING: 0,
    IN_PROGRESS: 0,
    COMPLETED: 0,
  };

  const byState = useMemo(() => {
    const buckets: Record<RequalificationState, RequalScheduleRow[]> = {
      PENDING: [],
      IN_PROGRESS: [],
      COMPLETED: [],
    };
    for (const row of items) {
      buckets[row.state].push(row);
    }
    return buckets;
  }, [items]);

  return (
    <div className={wstyles.adminScreen}>
      <PageHero
        eyebrow={t('equipment.requalDashboard.eyebrow', {
          defaultValue: 'Equipment',
        })}
        title={t('equipment.requalDashboard.title', {
          defaultValue: 'Requalification schedule',
        })}
        subtitle={t('equipment.requalDashboard.subtitle', {
          defaultValue:
            'Kanban view of requalifications. Validation leads pick up Pending tasks; closing a task requires a signed REQUAL qualification document.',
        })}
      />

      <Card>
        <CardBody>
          {scheduleQ.isLoading && (
            <div
              role="status"
              aria-live="polite"
              className={styles.board}
              aria-label={t('common.loading', { defaultValue: 'Loading…' })}
            >
              {[0, 1, 2].map((c) => (
                <div key={c} className={styles.column}>
                  {[0, 1].map((i) => (
                    <div
                      key={`${c}-${i}`}
                      className={styles.skeletonCard}
                      aria-hidden="true"
                    />
                  ))}
                </div>
              ))}
            </div>
          )}

          {scheduleQ.isError && (
            <div className={styles.errorBanner} role="alert">
              <span>
                {t('equipment.requalDashboard.errors.loadFailed', {
                  defaultValue: 'Could not load the requalification schedule.',
                })}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => scheduleQ.refetch()}
              >
                {t('equipment.requalDashboard.retry', { defaultValue: 'Retry' })}
              </Button>
            </div>
          )}

          {!scheduleQ.isLoading && !scheduleQ.isError && (
            <div
              className={styles.board}
              role="region"
              aria-label={t('equipment.requalDashboard.boardAria', {
                defaultValue: 'Requalification Kanban board',
              })}
            >
              {STATES.map((state) => (
                <RequalKanbanColumn
                  key={state}
                  state={state}
                  title={t(`equipment.requalDashboard.columns.${state}`, {
                    defaultValue: STATE_DEFAULT_TITLE[state],
                  })}
                  rows={byState[state]}
                  count={counts[state] ?? byState[state].length}
                  onStart={(row) => setDialog({ kind: 'start', row })}
                  onComplete={(row) => setDialog({ kind: 'complete', row })}
                />
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {dialog.kind === 'start' && (
        <StartRequalificationDialog
          row={dialog.row}
          onClose={() => setDialog({ kind: 'closed' })}
          onStarted={() => {
            setDialog({ kind: 'closed' });
            scheduleQ.refetch();
          }}
        />
      )}

      {dialog.kind === 'complete' && (
        <CompleteRequalificationDialog
          row={dialog.row}
          onClose={() => setDialog({ kind: 'closed' })}
          onCompleted={() => {
            setDialog({ kind: 'closed' });
            scheduleQ.refetch();
          }}
        />
      )}
    </div>
  );
}
